import { formatMedianDays } from './crowsNestTimeToConversion'
import { CROWS_NEST_UNADOPTED_PLAN_FILTERS } from './crowsNestUnadoptedVenues'

/**
 * Crow’s Nest — Ollie findings by check. Pure mapping for the table.
 * Missing data stays "—"; a rate with nobody shown is never 0%.
 */

export const CROWS_NEST_OLLIE_STATUS_LABELS = Object.freeze({
  needs_work: 'Needs work',
  helping: 'Helping',
  too_early: 'Too early',
})

/** A snooze rate this high, once enough people have seen it, reads as noise. */
export const CROWS_NEST_OLLIE_NOISY_SNOOZE = Object.freeze({
  rate: 0.5,
  minShownPeople: 10,
})

export const CROWS_NEST_OLLIE_PLAN_FILTERS = CROWS_NEST_UNADOPTED_PLAN_FILTERS

export const CROWS_NEST_OLLIE_DEFAULT_RANGE_DAYS = 30

const COUNT_KEYS = Object.freeze([
  'found',
  'shown',
  'clicked',
  'actionConfirmed',
  'draftUsed',
  'snoozed',
  'dismissed',
  'completed',
  'fixed',
  'reportedWrong',
])

const whole = value => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0
}

export function formatOllieRate(rate) {
  if (rate == null || rate === '') return '—'
  const n = Number(rate)
  if (!Number.isFinite(n)) return '—'
  const pct = n * 100
  return `${pct < 10 && pct % 1 !== 0 ? pct.toFixed(1) : Math.round(pct)}%`
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

const mapCounts = counts => ({
  events: whole(counts?.events),
  venues: whole(counts?.venues),
  people: whole(counts?.people),
})

const rateOrNull = value =>
  value == null || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value)

export function isNoisySnoozeRate(
  snoozedRate,
  shownPeople,
  threshold = CROWS_NEST_OLLIE_NOISY_SNOOZE,
) {
  return (
    snoozedRate != null &&
    snoozedRate >= threshold.rate &&
    shownPeople >= threshold.minShownPeople
  )
}

export function mapOllieFindingsCheckRow(row = {}) {
  const found = mapCounts(row.found)
  const shown = mapCounts(row.shown)
  const fixed = mapCounts(row.fixed)
  const reportedWrong = mapCounts(row.reportedWrong)
  const alreadySorted = mapCounts(row.alreadySorted)
  const snoozed = mapCounts(row.snoozed)
  const actedOn = mapCounts(row.actedOn)
  const snoozedRate = rateOrNull(row.snoozedRate)
  const actedOnRate = rateOrNull(row.actedOnRate)
  const hasActivity =
    typeof row.hasActivity === 'boolean'
      ? row.hasActivity
      : COUNT_KEYS.some(key => whole(row[key]?.events) > 0)
  const rawMedian = row.timeToFix?.medianDays
  const medianDays =
    rawMedian == null || !Number.isFinite(Number(rawMedian))
      ? null
      : Number(rawMedian)
  const fixSample = whole(row.timeToFix?.sampleSize)
  const status = CROWS_NEST_OLLIE_STATUS_LABELS[row.status]
    ? row.status
    : 'too_early'
  return {
    key: row.checkId || 'unknown',
    checkId: row.checkId || null,
    name: row.name || row.checkId || 'Unknown check',
    kind: row.kind || null,
    enabled: row.enabled !== false,
    registered: row.registered !== false,
    found,
    foundLabel: plural(found.venues, 'venue', 'venues'),
    shown,
    shownLabel: plural(shown.people, 'person', 'people'),
    fixed,
    fixedLabel: String(fixed.events),
    fixedDetail: fixed.events
      ? `${plural(fixed.venues, 'venue', 'venues')} · ${formatOllieRate(
          row.fixedWithinTargetRate,
        )} in target`
      : null,
    timeToFix: { medianDays, sampleSize: fixSample },
    timeToFixLabel: formatMedianDays(medianDays),
    timeToFixSample: plural(fixSample, 'fix', 'fixes'),
    reportedWrong,
    reportedWrongLabel: String(reportedWrong.people),
    reportedWrongRateLabel: formatOllieRate(row.reportedWrongRate),
    reportedWrongRate:
      row.reportedWrongRate == null ? null : Number(row.reportedWrongRate),
    alreadySorted,
    alreadySortedLabel: alreadySorted.events
      ? `${alreadySorted.events} already sorted (not counted)`
      : null,
    snoozed,
    snoozedLabel: String(snoozed.people),
    snoozedRate,
    snoozedRateLabel: formatOllieRate(snoozedRate),
    noisy:
      typeof row.noisy === 'boolean'
        ? row.noisy
        : isNoisySnoozeRate(snoozedRate, shown.people),
    actedOn,
    actedOnLabel: String(actedOn.people),
    actedOnRate,
    actedOnRateLabel: formatOllieRate(actedOnRate),
    hasActivity,
    status,
    statusLabel: CROWS_NEST_OLLIE_STATUS_LABELS[status],
    reason: row.reason || null,
  }
}

/** The table body is wider than its box (1px slack for sub-pixel rounding). */
export function isOllieTableOverflowing(body) {
  if (!body) return false
  return Number(body.scrollWidth) > Number(body.clientWidth) + 1
}

/** Rows with something in range first; the rest fold into "No activity yet". */
export function splitOllieFindingsByActivity(rows = []) {
  return {
    active: rows.filter(row => row.hasActivity),
    quiet: rows.filter(row => !row.hasActivity),
  }
}

const planValues = CROWS_NEST_OLLIE_PLAN_FILTERS.map(option => option.value)

/**
 * @param {{ fromDate?: string, toDate?: string, plan?: string }} filters
 *   UK calendar days (YYYY-MM-DD); blank plan is every plan.
 */
export function buildOllieFindingsQuery({ fromDate, toDate, plan } = {}) {
  const query = {}
  if (fromDate) query.fromDate = fromDate
  if (toDate) query.toDate = toDate
  if (plan && planValues.includes(plan)) query.plan = plan
  return query
}

export function ollieFindingsPlanLabel(plan) {
  const option = CROWS_NEST_OLLIE_PLAN_FILTERS.find(
    entry => entry.value === (plan || ''),
  )
  return option ? option.label : 'All plans'
}

/** Mirrors Express `OLLIE_FINDINGS_TOTAL_*`: same labels and units as the weekly email. */
export const OLLIE_FINDINGS_TOTAL_HEADLINE = Object.freeze([
  {
    key: 'found',
    label: 'Found at',
    unit: ['venue', 'venues'],
    event: 'Found',
  },
  {
    key: 'shown',
    label: 'Shown to',
    unit: ['person', 'people'],
    event: 'Card shown',
  },
  {
    key: 'snoozed',
    label: 'Snoozed by',
    unit: ['person', 'people'],
    event: 'Snoozed',
  },
  {
    key: 'actedOn',
    label: 'Acted on by',
    unit: ['person', 'people'],
    event: 'Acted on',
  },
  { key: 'fixed', label: 'Fixed', unit: ['fix', 'fixes'], event: 'Fixed' },
  {
    key: 'reportedWrong',
    label: 'Reported wrong by',
    unit: ['person', 'people'],
    event: 'Reported wrong',
  },
])

const mapTotalHeadline = totals =>
  Object.fromEntries(
    OLLIE_FINDINGS_TOTAL_HEADLINE.map(({ key, label, unit, event }) => {
      const hasEvents = totals.events?.[key] != null
      const events = hasEvents ? whole(totals.events[key]) : null
      const value = whole(totals[key])
      return [
        key,
        {
          label,
          value,
          unitWord: value === 1 ? unit[0] : unit[1],
          text: plural(value, unit[0], unit[1]),
          events,
          eventsText: hasEvents
            ? `${event} ${plural(events, 'time', 'times')}`
            : null,
        },
      ]
    }),
  )

export function mapCrowsNestOllieFindings(response = {}) {
  const totals = response.totals || {}
  const rawMedian = totals.timeToFix?.medianDays
  const medianDays =
    rawMedian == null || !Number.isFinite(Number(rawMedian))
      ? null
      : Number(rawMedian)
  const fixSample = whole(totals.timeToFix?.sampleSize)
  return {
    range: response.range || null,
    filters: response.filters || {},
    totals: {
      found: whole(totals.found),
      shown: whole(totals.shown),
      snoozed: whole(totals.snoozed),
      actedOn: whole(totals.actedOn),
      fixed: whole(totals.fixed),
      reportedWrong: whole(totals.reportedWrong),
      alreadySorted: whole(totals.alreadySorted),
      needsWork: whole(totals.needsWork),
      helping: whole(totals.helping),
      timeToFixLabel: formatMedianDays(medianDays),
      timeToFixSample: plural(fixSample, 'fix', 'fixes'),
      headline: mapTotalHeadline(totals),
    },
    byCheck: (Array.isArray(response.byCheck) ? response.byCheck : []).map(
      mapOllieFindingsCheckRow,
    ),
  }
}
